import type { Position } from 'components/map/types';

const RDP_EPSILONS = [0.00005, 0.0001, 0.0002, 0.0005, 0.001, 0.005];

function toRad(deg: number): number {
    return (deg * Math.PI) / 180;
}

export function haversineMeters(a: Position, b: Position): number {
    const R = 6_371_000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function lerp(a: Position, b: Position, t: number): Position {
    return {
        lat: a.lat + (b.lat - a.lat) * t,
        lng: a.lng + (b.lng - a.lng) * t,
    };
}

function perpendicularDistance(p: Position, a: Position, b: Position): number {
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    if (dx === 0 && dy === 0) {
        return Math.sqrt((p.lng - a.lng) ** 2 + (p.lat - a.lat) ** 2);
    }
    const t = ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
    return Math.sqrt((p.lng - a.lng - t * dx) ** 2 + (p.lat - a.lat - t * dy) ** 2);
}

function rdpRecurse(positions: Position[], epsilon: number, start: number, end: number, keep: Set<number>): void {
    const a = positions[start];
    const b = positions[end];
    if (end - start < 2 || !a || !b) return;
    let maxDist = 0;
    let maxIdx = start + 1;
    for (let i = start + 1; i < end; i++) {
        const p = positions[i];
        const d = p ? perpendicularDistance(p, a, b) : 0;
        if (d > maxDist) {
            maxDist = d;
            maxIdx = i;
        }
    }
    if (maxDist > epsilon) {
        keep.add(maxIdx);
        rdpRecurse(positions, epsilon, start, maxIdx, keep);
        rdpRecurse(positions, epsilon, maxIdx, end, keep);
    }
}

// Returns the set of indices to keep after RDP simplification.
export function rdpKeptIndices(positions: Position[], epsilon: number): Set<number> {
    if (positions.length <= 2) return new Set(positions.keys());
    const keep = new Set([0, positions.length - 1]);
    rdpRecurse(positions, epsilon, 0, positions.length - 1, keep);
    return keep;
}

// Returns the indices kept by the smallest RDP tolerance that leaves at most maxPoints.
// Falls back to uniform sampling (always keeping first and last) if even the largest tolerance keeps too many.
export function simplifyToMax(positions: Position[], maxPoints: number): Set<number> {
    for (const epsilon of RDP_EPSILONS) {
        const kept = rdpKeptIndices(positions, epsilon);
        if (kept.size <= maxPoints) return kept;
    }
    const step = Math.ceil(positions.length / (maxPoints - 1));
    return new Set([...positions.keys()].filter(i => i % step === 0 || i === positions.length - 1));
}

export function fitToSimulation(positions: Position[], stepMeters: number): Position[] {
    const first = positions.at(0);
    const last = positions.at(-1);
    if (!first || !last || positions.length < 2) return positions;

    const result: Position[] = [first];
    let accumulated = 0;

    for (let i = 0; i < positions.length - 1; i++) {
        const a = positions[i];
        const b = positions[i + 1];
        if (!a || !b) continue;
        const segmentLen = haversineMeters(a, b);

        if (segmentLen === 0) continue;

        let distIntoSegment = stepMeters - accumulated;

        while (distIntoSegment <= segmentLen) {
            result.push(lerp(a, b, distIntoSegment / segmentLen));
            distIntoSegment += stepMeters;
        }

        accumulated = segmentLen - (distIntoSegment - stepMeters);
    }

    const prev = result.at(-1);
    if (prev && (prev.lat !== last.lat || prev.lng !== last.lng)) {
        result.push(last);
    }

    return result;
}
